import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs } from "@/components/ui/atoms/Tabs";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "@/components/ui/atoms";
import { Skeleton } from "@/components/ui/atoms/Skeleton";

const SCROLL_AMOUNT = 200;
const SKELETON_TAB_COUNT = 6;
/** Matches line Tabs trigger height (h-11). */
const TAB_SKELETON_HEIGHT_CLASS = "h-11";
const TAB_SKELETON_WIDTHS = ["w-12", "w-20", "w-20", "w-16", "w-24", "w-20"] as const;

interface FilterTab {
  id: string;
  label: string;
}

interface FilterToolbarProps {
  tabs: FilterTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  tabsLoading?: boolean;
}

export function FilterToolbar({
  tabs,
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  tabsLoading = false,
}: FilterToolbarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();

    const element = scrollRef.current;
    if (!element) return;

    element.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [tabs, tabsLoading, updateScrollState]);

  const scrollTabs = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };

  return (
    <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
      <div className="flex w-full min-w-0 items-center gap-1 border-b border-dark-invirtus-soft">
        {canScrollLeft ? (
          <Button
            type="icon"
            intent="muted"
            size="sm"
            htmlType="button"
            aria-label="Scroll tabs left"
            icon={
              <IconWrapper size="sm">
                <ChevronLeft />
              </IconWrapper>
            }
            onClick={() => scrollTabs("left")}
          />
        ) : null}

        <div
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabsLoading ? (
            <div
              className="flex w-fit min-w-max items-end gap-5"
              role="status"
              aria-label="Loading categories"
              aria-busy="true"
            >
              {Array.from({ length: SKELETON_TAB_COUNT }, (_, index) => (
                <Skeleton
                  key={index}
                  decorative
                  className={`mb-0 shrink-0 ${TAB_SKELETON_HEIGHT_CLASS} ${TAB_SKELETON_WIDTHS[index] ?? "w-20"}`}
                />
              ))}
            </div>
          ) : (
            <Tabs
              aria-label="Blog categories"
              orientation="horizontal"
              variant="line"
              size="md"
              color="primary"
              value={activeTab}
              onValueChange={onTabChange}
              items={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
              className="w-fit min-w-max"
            />
          )}
        </div>

        {canScrollRight ? (
          <Button
            type="icon"
            intent="muted"
            size="sm"
            htmlType="button"
            aria-label="Scroll tabs right"
            icon={
              <IconWrapper size="sm">
                <ChevronRight />
              </IconWrapper>
            }
            onClick={() => scrollTabs("right")}
          />
        ) : null}
      </div>

      <SearchInput
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Search blog..."
        showClearButton
        className="w-full md:w-[320px]"
      />
    </div>
  );
}
